/**
 * Checkout rail model — README §1 "Checkout rail" + the 12-variant table
 * (docs/plans/2026-09-events-clubs-redesign/handoff/README.md), copy from the `V` map in
 * `handoff/Event Page.dc.html`.
 *
 * Pure. Decides which variant the viewer is in and what the rail / sheet / CTA bar says.
 * Money is previewed with the kit's `computeBreakdown`; when a server quote is supplied it
 * wins (`prServiceFeeCents / gstCents / stripeFeeCents / grandTotalCents / balancePortionCents`).
 *
 * Variant precedence (PLAN.md "A1 ↔ A2 contract"):
 *   host > ended > hostwanted > external > held > soldout > guest > curated > tiered > pwyw > free > starter > guaranteed
 * plus `closed` for anything not on sale (canceled / dissolved / draft, and a declined application).
 */
import { looksLikePrice } from '../../utils/eventPricing';
import { computeBreakdown } from '../surface/MoneyBreakdown';
import { money, moneyWhole, dayLabel, dateLabel, ticketNoun, capacityLabel, plural } from '../../utils/surfaceFormat';

export const QTY_MIN = 1;
export const QTY_MAX = 10;
export const PWYW_CHIPS_CENTS = [1000, 1900, 3000, 5000];
export const ON_SALE_STATUSES = ['selling', 'funding', 'greenlit'];
export const GOING_STATUSES = ['accepted', 'paid'];

const up = (s) => (s || '').toString().toUpperCase();

/** `#PR-4471` from an application id — the last four hex chars, so it is stable per order. */
export function orderRef(applicationId) {
  if (!applicationId) return '';
  const hex = String(applicationId).replace(/-/g, '');
  return `PR-${hex.slice(-4).toUpperCase()}`;
}

export function isSoldOut(event) {
  const max = Number(event?.maxAttendees || 0);
  if (max <= 0) return false;
  return Number(event?.acceptedCount || 0) >= max;
}

export function spotsRemaining(event) {
  const max = Number(event?.maxAttendees || 0);
  if (max <= 0) return null;
  return Math.max(0, max - Number(event?.acceptedCount || 0));
}

export function isTieredEvent(event) {
  return event?.pricingMode === 'tiered' && Array.isArray(event?.tiers) && event.tiers.length > 0;
}

export function isPwywEvent(event) {
  return event?.pricingMode === 'pay_what_you_want' && !event?.isExternalTicketing;
}

/** A truly free event is $0 AND not pay-what-you-want (PWYW guests still pick an amount). */
export function isFreeEvent(event) {
  const base = isTieredEvent(event) ? (event?.displayPriceCents || 0) : (event?.ticketPriceCents || 0);
  return base === 0 && !isPwywEvent(event);
}

export function isViewerHost(event, viewer) {
  if (!viewer?.isAuthenticated || !event) return false;
  if (event.viewerCanEdit) return true;
  const pid = viewer.profile?.id;
  return !!pid && pid === event.hostId;
}

export function canBuyMore(event) {
  return !event?.isExternalTicketing && ON_SALE_STATUSES.includes(event?.status);
}

export function defaultTier(event) {
  if (!isTieredEvent(event)) return null;
  return event.tiers.find((t) => t.isAvailable) || null;
}

export function resolveTier(event, selectedTierId) {
  if (!isTieredEvent(event)) return null;
  const picked = selectedTierId ? event.tiers.find((t) => t.id === selectedTierId && t.isAvailable) : null;
  return picked || defaultTier(event);
}

export function pwywMinCents(event) {
  return Math.max(0, Number(event?.ticketPriceCents || 0));
}

export function pwywDefaultCents(event) {
  const suggested = Number(event?.suggestedPriceCents || 0);
  const min = pwywMinCents(event);
  return Math.max(min, suggested || min);
}

/** Logged-out checkout eligibility mirrors the current page (inventory §2.4). */
export function guestEligibility(event) {
  const isOpen = event?.mode === 'open';
  const isGuaranteed = event?.fundingType === 'guaranteed';
  const isPaid = (event?.ticketPriceCents || 0) > 0 || isPwywEvent(event) || (isTieredEvent(event) && (event?.displayPriceCents || 0) > 0);
  return {
    canGuestCheckout: isOpen && isGuaranteed && isPaid,
    canFreeRsvp: isOpen && !isPaid,
  };
}

/**
 * Which of the 14 variants the viewer is in. `ignoreApplication` is the BUY MORE TICKETS
 * path: the viewer already holds tickets but wants the purchase flow again.
 */
export function detectVariant({ event, application, viewer, ignoreApplication = false }) {
  if (!event) return 'closed';
  const auth = !!viewer?.isAuthenticated;
  const app = ignoreApplication ? null : application;
  const status = app?.status;

  if (isViewerHost(event, viewer)) return 'host';
  // We never sold the tickets, so there is nothing to rate or verify once it is over.
  if (event.isExternalTicketing && event.status === 'ended') return 'closed';
  if (event.status === 'ended') return 'ended';
  if (event.isHostWanted) return 'hostwanted';
  if (event.isExternalTicketing) return 'external';
  if (!ON_SALE_STATUSES.includes(event.status)) return 'closed';
  if (status === 'declined') return 'closed';
  if (GOING_STATUSES.includes(status)) return 'held';
  if (status === 'waitlisted') return 'soldout';
  if (isSoldOut(event) && !app) return 'soldout';
  if (!auth) {
    const { canGuestCheckout, canFreeRsvp } = guestEligibility(event);
    if (canGuestCheckout) return 'guest';
    if (canFreeRsvp) return 'free';
    // Curated / crowdfunded: gated to CREATE ACCOUNT / SIGN IN, keeps the variant's band.
    if (event.mode === 'curated') return 'curated';
    if (event.fundingType === 'party_starter' && event.status === 'funding') return 'starter';
    return 'guaranteed';
  }
  if (event.mode === 'curated') return 'curated';
  if (isTieredEvent(event)) return 'tiered';
  if (isPwywEvent(event)) return 'pwyw';
  if (isFreeEvent(event)) return 'free';
  if (event.fundingType === 'party_starter' && event.status === 'funding') return 'starter';
  return 'guaranteed';
}

function tierNote(t) {
  if (t.unavailableReason === 'sold_out') return 'Sold out';
  if (t.unavailableReason === 'ended') return 'Ended';
  if (t.unavailableReason === 'inactive') return 'Not available';
  if (!t.isAvailable) return 'Not available';
  const bits = [];
  if (t.remainingCount != null) bits.push(`${t.remainingCount} left`);
  if (t.salesEndAt) bits.push(`until ${new Date(t.salesEndAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
  return bits.join(' · ') || 'On sale now';
}

/** Progress numbers for a Party Starter: cash goal or RSVP goal. */
export function fundingBlock(event) {
  const goalType = event?.goalType === 'rsvp' ? 'rsvp' : 'cash';
  const pledgedCents = Number(event?.totalPledgedCents || 0);
  const goalCents = Number(event?.fundingGoalCents || 0);
  const count = Number(event?.acceptedCount || 0);
  const max = goalType === 'rsvp' ? Number(event?.rsvpGoal || event?.maxAttendees || 0) : Number(event?.maxAttendees || 0);
  const pct = goalType === 'rsvp'
    ? (max > 0 ? Math.min(100, Math.round((count / max) * 100)) : 0)
    : (goalCents > 0 ? Math.min(100, Math.round((pledgedCents / goalCents) * 100)) : 0);
  return { pledgedCents, goalCents, count, max, deadline: event?.fundingDeadline, pct, goalType };
}

/**
 * The refund card under the rail (IF PLANS CHANGE), computed from the event's flake settings
 * and what the server will actually let the guest do. A ticket is a real card charge and
 * `WithdrawApplicationAsync` refuses a paid application, so on a Guaranteed event — paid at
 * checkout — there is no "you withdraw" line: tickets are final unless the host cancels or
 * issues a refund. Withdrawing (with the flake rule) only exists while money is committed
 * but not settled: a Party Starter before greenlight, or a curated application the host has
 * not yet decided on.
 */
export function refundRows(event, application = null) {
  const rows = [];
  if (!event) return rows;
  const free = isFreeEvent(event);
  const curated = event.mode === 'curated';
  const starter = event.fundingType === 'party_starter';
  if (free) {
    rows.push({ k: 'Change your mind', v: 'no charge, ever' });
    if (curated) rows.push({ k: 'Not picked by the host', v: 'nothing owed' });
    return rows;
  }
  if (starter) rows.push({ k: 'It never funds', v: 'refunded to wallet' });
  if (curated) rows.push({ k: 'Declined by the host', v: 'full refund' });
  rows.push({ k: 'Host cancels', v: 'full refund' });
  const paid = application?.status === 'paid';
  const withdrawable = !paid && (starter || curated);
  if (!withdrawable) {
    rows.push({ k: 'You change your mind', v: 'tickets are final' });
    return rows;
  }
  const mode = event.flakeFeeMode || 'percentage';
  if (mode === 'none') rows.push({ k: 'You withdraw', v: 'full refund' });
  else if (mode === 'fixed') rows.push({ k: 'You withdraw', v: `host keeps ${money(event.flakeFeeFixedCents || 0)}` });
  else rows.push({ k: 'You withdraw', v: `host keeps ${event.flakeFeePercentage ?? 6}%` });
  return rows;
}

/** Flake penalty for withdrawing — the page's existing rule, unchanged. */
export function flakePenaltyCents(event, application) {
  const committed = application?.partyCreditsCommitted || application?.pledgeAmountCents || 0;
  if (!committed) return { committed: 0, penalty: 0, refund: 0 };
  let penalty = 0;
  if (event?.flakeFeeMode === 'percentage') penalty = Math.round((committed * (event?.flakeFeePercentage || 6)) / 100);
  else if (event?.flakeFeeMode === 'fixed') penalty = Math.min(event?.flakeFeeFixedCents || 0, committed);
  return { committed, penalty, refund: committed - penalty };
}

/**
 * The whole rail, as data.
 *
 * @param {object} p
 * @param {object} p.event            EventDto
 * @param {object|null} p.application the viewer's ApplicationDto (or the one just created)
 * @param {object} p.viewer           { isAuthenticated, profile, walletBalance, refreshWallet }
 * @param {number} p.quantity
 * @param {string|null} p.selectedTierId
 * @param {number|null} p.pwywCents
 * @param {number|null} p.waitlistPosition  known position (guest waitlist reply, or null)
 * @param {object|null} p.quote       server checkout quote (wins over the preview math)
 * @param {object|null} p.promo       applied promo { valid, discountedPriceCents, code }
 * @param {boolean} p.ignoreApplication  BUY MORE TICKETS — run the purchase variant again
 * @param {boolean} p.goldenTicketsEnabled
 * @param {object|null} p.claimInfo   partyEngineService.getClaimInfo data (host wanted)
 */
export function railModel({
  event,
  application = null,
  viewer = {},
  quantity = 1,
  selectedTierId = null,
  pwywCents = null,
  waitlistPosition = null,
  quote = null,
  promo = null,
  ignoreApplication = false,
  goldenTicketsEnabled = true,
  claimInfo = null,
} = {}) {
  const variant = detectVariant({ event, application, viewer, ignoreApplication });
  const auth = !!viewer?.isAuthenticated;
  const app = ignoreApplication ? null : application;
  const appStatus = app?.status || null;
  const curated = event?.mode === 'curated';
  const free = isFreeEvent(event);
  const tiered = isTieredEvent(event);
  const pwyw = isPwywEvent(event);
  const gated = !auth && ['curated', 'starter', 'guaranteed'].includes(variant) && !guestEligibility(event).canGuestCheckout;
  const qty = Math.max(QTY_MIN, Math.min(QTY_MAX, Number(quantity) || 1));

  // ── unit price ──────────────────────────────────────────────────────────
  const tier = tiered ? resolveTier(event, selectedTierId) : null;
  let unitCents = 0;
  if (tiered) unitCents = tier?.priceCents ?? 0;
  else if (pwyw) unitCents = pwywCents == null ? pwywDefaultCents(event) : Math.max(0, Number(pwywCents) || 0);
  else unitCents = Number(event?.ticketPriceCents || 0);
  if (promo?.valid && promo.discountedPriceCents != null && !tiered) unitCents = Number(promo.discountedPriceCents);
  const pwywMin = pwyw ? pwywMinCents(event) : 0;
  const pwywBelowMin = pwyw && unitCents < pwywMin;

  // ── money ───────────────────────────────────────────────────────────────
  const preview = computeBreakdown({ unitCents, qty });
  const usingQuote = !!quote && typeof quote.grandTotalCents === 'number';
  const ticketsCents = usingQuote ? (quote.ticketCents ?? preview.tickets) : preview.tickets;
  const feeCents = usingQuote ? (quote.prServiceFeeCents ?? 0) : preview.fee;
  const gstCents = usingQuote ? (quote.gstCents ?? 0) : preview.gst;
  const procCents = usingQuote ? (quote.stripeFeeCents ?? 0) : preview.proc;
  const totalCents = usingQuote ? quote.grandTotalCents : preview.total;
  const walletBalance = Number(viewer?.walletBalance || 0);
  let walletAppliedCents = 0;
  if (usingQuote) walletAppliedCents = Number(quote.balancePortionCents || 0);
  else if (auth && unitCents > 0) walletAppliedCents = Math.min(walletBalance, totalCents);
  const cardCents = Math.max(0, totalCents - walletAppliedCents);

  const lines = unitCents > 0 ? [
    { k: qty > 1 ? `${tier ? up(tier.name) : 'TICKET'} × ${qty}` : (tier ? up(tier.name) : 'TICKET'), v: money(ticketsCents) },
    ...(usingQuote && quote.promoDiscountTotalCents > 0 ? [{ k: `PROMO ${up(quote.promoCode)}`, v: money(-quote.promoDiscountTotalCents) }] : []),
    { k: 'PORTAL SERVICE FEE', v: money(feeCents) },
    { k: 'GST (5%)', v: money(gstCents) },
    { k: 'PROCESSING', v: money(procCents) },
  ] : [];

  const seatNoun = ticketNoun(qty, curated ? 'SEAT' : 'TICKET');
  const buyLabel = `BUY ${seatNoun} — ${money(totalCents)}`;

  // ── defaults (RAIL_BASE) ────────────────────────────────────────────────
  const r = {
    variant,
    band: 'GET TICKETS',
    bandRight: '',
    price: unitCents > 0 ? money(unitCents) : 'FREE',
    priceNote: 'PER TICKET',
    qty: { show: true, label: 'TICKETS', min: QTY_MIN, max: QTY_MAX },
    breakdown: { show: unitCents > 0, lines, totalCents, walletAppliedCents, cardCents },
    cta: { label: buyLabel, fill: true, disabled: false },
    cta2: null,
    fine: 'One card charge. Your wallet balance is used first.',
    escrow: 'Your money sits with Portal until 24h after doors, then goes to the host.',
    mobCta: 'GET TICKETS',
    mobNote: 'PER TICKET',
    unitCents,
    totalCents,
    funding: null,
    held: null,
    tiers: null,
    pwyw: null,
    guest: false,
    gated,
    applied: null,
    resume: null,
    houseRules: Array.isArray(event?.houseRules) ? event.houseRules : [],
    goldenTicket: null,
    refund: refundRows(event, app),
    signIn: !auth,
  };

  const spots = spotsRemaining(event);
  const cap = capacityLabel(event?.acceptedCount || 0, event?.maxAttendees || 0);

  switch (variant) {
    case 'host': {
      r.band = 'YOUR EVENT';
      r.bandRight = `${cap} IN`;
      r.price = free ? 'FREE' : money(event.displayPriceCents ?? event.ticketPriceCents ?? 0);
      r.priceNote = free ? 'NO CHARGE' : (event.hasMultiplePrices ? 'FROM' : 'PER TICKET');
      r.qty.show = false;
      r.breakdown.show = false;
      r.cta = { label: 'MANAGE THIS EVENT', fill: true, disabled: false };
      r.fine = `${up(event.status)} · ${plural(Number(event.acceptedCount || 0), 'GUEST')} in.`;
      r.escrow = free ? null : 'Ticket money sits in escrow until 24h after doors, then it is yours to withdraw.';
      r.mobCta = 'MANAGE';
      r.mobNote = 'YOUR EVENT';
      break;
    }
    case 'ended': {
      const went = GOING_STATUSES.includes(appStatus);
      r.band = 'HOW WAS IT';
      r.bandRight = 'GUESTS ONLY';
      r.price = 'RATE IT';
      r.priceNote = 'TAKES 20 SECONDS';
      r.qty.show = false;
      r.breakdown.show = false;
      r.cta = { label: 'CONFIRM I WENT + RATE', fill: true, disabled: !went };
      r.cta2 = went ? { label: 'SOMETHING WENT WRONG' } : null;
      r.fine = went
        ? 'Your rating is what makes a host escrow-verified. It is public; your name is not.'
        : 'Only people who held a ticket can rate this one.';
      r.escrow = free ? null : (event.escrowReleaseAt && new Date(event.escrowReleaseAt) > new Date()
        ? `Funds are released to the host on ${dateLabel(event.escrowReleaseAt)}, 24h after doors.`
        : 'Funds were released to the host 24h after doors.');
      r.mobCta = 'RATE IT';
      r.mobNote = went ? 'YOU WENT' : 'GUESTS ONLY';
      break;
    }
    case 'hostwanted': {
      const eligible = !!claimInfo?.viewerEligible;
      r.band = 'CLAIM THIS EVENT';
      r.bandRight = 'UNCLAIMED';
      r.price = moneyWhole(event.fundingGoalCents || 0);
      r.priceNote = 'GOAL ATTACHED';
      r.qty.show = false;
      r.breakdown.show = false;
      if (!auth) r.cta = { label: 'SIGN IN TO CLAIM', fill: true, disabled: false };
      else if (claimInfo && !eligible) r.cta = { label: 'CLAIM IT — I WILL HOST', fill: true, disabled: true };
      else r.cta = { label: 'CLAIM IT — I WILL HOST', fill: true, disabled: false };
      r.cta2 = { label: "I'M INTERESTED, NOT HOSTING" };
      r.fine = claimInfo && auth && !eligible && claimInfo.reason
        ? claimInfo.reason
        : 'Claiming puts the page, the money and the list in your name.';
      r.escrow = 'Money already pledged moves into your escrow when you claim and is released 24h after doors.';
      r.mobCta = 'CLAIM IT';
      r.mobNote = 'NEEDS A HOST';
      break;
    }
    case 'external': {
      const interested = appStatus === 'external_interest';
      r.band = 'GET TICKETS';
      r.bandRight = 'OFF PLATFORM';
      r.price = (looksLikePrice(event.externalTicketDisplayPrice) && event.externalTicketDisplayPrice.trim()) || 'SEE TICKETS';
      r.priceNote = 'PRICED BY THE SELLER';
      r.qty.show = false;
      r.breakdown.show = false;
      r.cta = { label: 'GET TICKETS ↗', fill: true, disabled: false };
      r.cta2 = auth ? { label: interested ? 'REMOVE ME FROM THE GUEST LIST' : 'ADD ME TO THE GUEST LIST' } : null;
      r.fine = auth
        ? (interested ? 'You are on the guest list. Your card never touches Portal.' : 'Clicking through tells the host you went. Your card never touches Portal.')
        : 'Sign in to be added to the guest list. Your card never touches Portal.';
      r.escrow = null;
      r.mobCta = 'GET TICKETS ↗';
      r.mobNote = 'ANOTHER SITE';
      r.applied = interested ? 'external_interest' : null;
      break;
    }
    case 'held': {
      const tickets = Number(app?.quantity || 1);
      const paidCents = app?.partyCreditsCommitted || (app?.pledgeAmountCents || 0) * tickets;
      const paid = appStatus === 'paid';
      r.band = 'YOUR TICKETS';
      r.bandRight = `ORDER #${orderRef(app?.id)}`;
      r.price = ticketNoun(tickets).replace(/^1 /, '1 ');
      r.priceNote = free ? 'FREE' : `${paid ? 'PAID' : 'PLEDGED'} ${money(paidCents)}`;
      r.qty.show = false;
      r.breakdown.show = false;
      r.held = { tickets, paidCents, orderRef: orderRef(app?.id), date: dayLabel(event.dateTime), status: appStatus, paid };
      r.cta = { label: 'SHOW MY TICKETS', fill: true, disabled: false };
      r.cta2 = canBuyMore(event) ? { label: free ? 'BRING SOMEONE' : 'BUY MORE TICKETS' } : null;
      r.fine = free ? 'You are on the list. Bring your name; the host has it.' : 'Your QR is in My Tickets and in your wallet app. Bring either one.';
      r.escrow = free ? null : (event.status === 'funding'
        ? 'Your money is in escrow until the host greenlights. If it never funds, it comes back to your wallet.'
        : 'Your money is in escrow until 24h after doors.');
      r.mobCta = 'SHOW TICKETS';
      r.mobNote = `${tickets} · ${free ? 'FREE' : (paid ? 'PAID' : 'PLEDGED')}`;
      // Withdraw is still allowed on an accepted-but-not-greenlit spot (the page's FLAKE button).
      r.applied = (appStatus === 'accepted' && !['greenlit', 'ended'].includes(event.status)) ? 'accepted' : appStatus;
      break;
    }
    case 'soldout': {
      const onList = appStatus === 'waitlisted';
      const pos = waitlistPosition != null ? Number(waitlistPosition) : null;
      r.band = onList ? 'ON THE WAITLIST' : 'JOIN THE WAITLIST';
      r.bandRight = pos != null ? (pos > 1 ? `${pos - 1} AHEAD OF YOU` : 'YOU ARE NEXT') : (onList ? 'WE MESSAGE IN ORDER' : 'WAITLIST IS OPEN');
      r.price = free ? 'FREE' : money(event.displayPriceCents ?? event.ticketPriceCents ?? 0);
      r.priceNote = 'IF A SPOT OPENS';
      r.qty = { show: !onList, label: 'SPOTS WANTED', min: QTY_MIN, max: QTY_MAX };
      r.breakdown.show = false;
      r.guest = !auth;
      r.cta = onList
        ? { label: 'YOU ARE ON THE WAITLIST', fill: true, disabled: true }
        : { label: 'JOIN THE WAITLIST', fill: true, disabled: false };
      r.cta2 = onList && auth ? { label: 'LEAVE THE WAITLIST' } : null;
      r.fine = onList
        ? 'No charge. When a spot opens, the waitlist gets a message in order and the first to answer takes it.'
        : 'No charge now. When a spot opens you get a message and first answer takes it.';
      r.escrow = null;
      r.mobCta = onList ? 'ON THE WAITLIST' : 'JOIN WAITLIST';
      r.mobNote = pos != null ? `#${pos}` : `${cap} IN`;
      r.applied = onList ? 'waitlisted' : null;
      break;
    }
    case 'guest': {
      r.band = 'GET TICKETS';
      r.bandRight = 'NO ACCOUNT NEEDED';
      r.guest = true;
      if (tiered) {
        r.tiers = event.tiers.map((t) => ({ id: t.id, name: up(t.name), note: tierNote(t), priceCents: t.priceCents, available: !!t.isAvailable, selected: tier?.id === t.id }));
        r.priceNote = tier ? up(tier.name) : 'PER TICKET';
      }
      r.cta = { label: buyLabel, fill: true, disabled: tiered && !tier };
      r.cta2 = { label: 'I ALREADY HAVE AN ACCOUNT' };
      r.fine = 'Ticket goes to your email. Make an account with that same email and it shows up there too.';
      r.mobCta = 'GET TICKETS';
      r.mobNote = 'PER TICKET';
      break;
    }
    case 'curated': {
      const applied = appStatus === 'pending';
      r.band = applied ? 'APPLICATION SENT' : 'APPLY FOR A SEAT';
      r.bandRight = applied ? 'HOST REVIEWS' : `${plural(Number(event.applicationCount || 0), 'APPLIED', 'APPLIED')}`;
      r.price = free ? 'FREE' : money(unitCents);
      r.priceNote = free ? 'NO CHARGE' : 'IF YOU GET IN';
      r.qty = { show: !applied && !gated, label: 'SEATS', min: QTY_MIN, max: QTY_MAX };
      r.breakdown.show = !applied && !gated && unitCents > 0;
      if (gated) {
        r.cta = { label: 'CREATE ACCOUNT', fill: true, disabled: false };
        r.cta2 = { label: 'SIGN IN' };
        r.fine = 'Sign up to apply with your Portal Page. The host reads every application.';
      } else if (applied) {
        r.cta = { label: 'UNDER REVIEW', fill: true, disabled: true };
        r.cta2 = { label: 'WITHDRAW MY APPLICATION' };
        r.fine = free
          ? 'The host reads every application and answers all of them, yes or no.'
          : 'You bought the seat. Declined applications are refunded in full, same day.';
      } else {
        r.cta = { label: free ? 'APPLY FOR A SEAT' : buyLabel, fill: true, disabled: false };
        const pro = viewer?.profile?.subscriptionTier === 'pro';
        const remaining = Number(viewer?.profile?.goldenTicketsRemaining || 0);
        if (goldenTicketsEnabled && !ignoreApplication) {
          if (pro && remaining > 0) {
            r.cta2 = { label: 'USE A GOLDEN TICKET' };
            r.goldenTicket = { eligible: true, remaining };
          } else if (!pro) {
            r.goldenTicket = { eligible: false, remaining: 0, upsell: true };
          }
        }
        r.fine = free
          ? 'Tell the host who you are. They read every one and answer all of them.'
          : 'You buy the seat now. Declined applications are refunded in full, same day.';
      }
      r.escrow = free ? null : 'Accepted means your money sits in escrow until 24h after doors. Declined means a full refund and you owe nothing.';
      r.mobCta = applied ? 'UNDER REVIEW' : 'APPLY';
      r.mobNote = 'HOST REVIEWS';
      r.applied = applied ? 'pending' : null;
      break;
    }
    case 'tiered': {
      const left = event.tiers.filter((t) => t.isAvailable).length;
      r.band = 'CHOOSE YOUR TICKET';
      r.bandRight = plural(left, 'TIER LEFT', 'TIERS LEFT');
      r.priceNote = tier ? up(tier.name) : 'PER TICKET';
      r.tiers = event.tiers.map((t) => ({ id: t.id, name: up(t.name), note: tierNote(t), priceCents: t.priceCents, available: !!t.isAvailable, selected: tier?.id === t.id }));
      r.cta = { label: buyLabel, fill: true, disabled: !tier };
      r.mobCta = 'CHOOSE TICKET';
      r.mobNote = `FROM ${money(event.displayPriceCents ?? unitCents)}`;
      break;
    }
    case 'pwyw': {
      r.band = 'PAY WHAT YOU WANT';
      r.bandRight = `MINIMUM ${money(pwywMin)}`;
      r.price = money(unitCents);
      r.priceNote = 'YOUR PICK';
      r.pwyw = { minCents: pwywMin, chips: PWYW_CHIPS_CENTS.filter((c) => c >= pwywMin), selectedCents: unitCents, belowMin: pwywBelowMin, suggestedCents: pwywDefaultCents(event) };
      if (unitCents === 0) {
        r.price = 'FREE';
        r.priceNote = 'YOUR PICK';
        r.cta = { label: `I'M COMING — FREE`, fill: true, disabled: false };
        r.breakdown.show = false;
      } else {
        r.cta = { label: buyLabel, fill: true, disabled: pwywBelowMin };
      }
      r.fine = `Anything at or above ${money(pwywMin)}. The host sees the number, nobody else does.`;
      r.mobCta = 'PICK AMOUNT';
      r.mobNote = 'YOU DECIDE';
      break;
    }
    case 'free': {
      r.band = 'RSVP';
      r.bandRight = spots != null ? `${spots} SPOTS LEFT` : '';
      r.price = 'FREE';
      r.priceNote = 'NO CHARGE';
      r.qty = { show: true, label: 'PEOPLE', min: QTY_MIN, max: auth ? QTY_MAX : Math.max(1, Math.min(QTY_MAX, spots ?? QTY_MAX)) };
      r.breakdown.show = false;
      r.guest = !auth;
      r.cta = { label: "I'M COMING", fill: true, disabled: false };
      r.cta2 = auth ? null : { label: 'I ALREADY HAVE AN ACCOUNT' };
      r.fine = auth ? 'Tell the host how many of you. You can change it any time.' : 'Your ticket is emailed. Sign up later with this email and it is waiting for you.';
      r.escrow = null;
      r.mobCta = 'RSVP';
      r.mobNote = 'FREE ENTRY';
      break;
    }
    case 'starter': {
      r.band = 'PLEDGE A SPOT';
      r.bandRight = free ? 'NO CHARGE' : 'HELD IN ESCROW';
      r.price = free ? 'FREE' : money(unitCents);
      r.priceNote = free ? 'RSVP TO COUNT' : 'YOUR PLEDGE';
      r.funding = fundingBlock(event);
      r.breakdown.show = !gated && unitCents > 0;
      r.qty.show = !gated;
      if (gated) {
        r.cta = { label: 'CREATE ACCOUNT', fill: true, disabled: false };
        r.cta2 = { label: 'SIGN IN' };
        r.fine = free
          ? 'Create a free account to grab your spot.'
          : "This event is crowdfunded — you'll need an account so we can refund you instantly if it doesn't happen.";
      } else {
        r.cta = { label: free ? "I'M COMING" : `PLEDGE ${money(totalCents)}`, fill: true, disabled: false };
        r.fine = free
          ? 'Your RSVP counts toward the goal. Nothing is charged.'
          : 'Charged now and held in escrow. It only reaches the host if they greenlight.';
      }
      r.escrow = free ? null : 'If it never funds, your ticket money comes back to your wallet.';
      r.mobCta = free ? 'RSVP' : 'PLEDGE';
      r.mobNote = 'IF IT FUNDS';
      break;
    }
    case 'closed': {
      const declined = appStatus === 'declined';
      if (declined) {
        const refunded = !!app?.partyCreditsRefunded;
        const committed = app?.partyCreditsCommitted || app?.pledgeAmountCents || 0;
        r.band = 'NOT THIS TIME';
        r.bandRight = 'DECLINED';
        r.price = committed > 0 ? (refunded ? 'REFUNDED' : 'DECLINED') : 'DECLINED';
        r.priceNote = committed > 0 ? (refunded ? `${money(committed)} BACK IN YOUR WALLET` : 'REFUND ON ITS WAY') : 'BY THE HOST';
        r.fine = committed > 0 ? 'Declined applications are refunded in full, same day.' : 'The host went another way this time.';
        r.applied = 'declined';
      } else if (event?.isExternalTicketing && event?.status === 'ended') {
        r.band = 'ENDED';
        r.bandRight = 'OFF PLATFORM';
        r.price = 'ENDED';
        r.priceNote = 'TICKETS WERE SOLD ELSEWHERE';
        r.fine = 'This one was listed from another site and has already happened.';
      } else {
        const s = event?.status;
        r.band = 'NOT ON SALE';
        r.bandRight = s === 'canceled' ? 'CANCELED' : s === 'dissolved' ? 'DID NOT FUND' : up(s || 'CLOSED');
        r.price = s === 'dissolved' ? 'REFUNDED' : 'CLOSED';
        r.priceNote = s === 'dissolved' ? 'EVERYONE PAID BACK' : 'NOT ACCEPTING APPLICATIONS';
        r.fine = 'Check back later or browse other events.';
      }
      r.qty.show = false;
      r.breakdown.show = false;
      r.cta = { label: 'BROWSE EVENTS', fill: false, disabled: false };
      r.escrow = null;
      r.mobCta = 'BROWSE EVENTS';
      r.mobNote = r.bandRight;
      break;
    }
    default: {
      // guaranteed
      const greenlit = event.status === 'greenlit';
      r.band = 'GET TICKETS';
      r.bandRight = spots != null && spots <= 10 ? `${spots} LEFT` : '';
      r.breakdown.show = !gated && unitCents > 0;
      r.qty.show = !gated;
      if (gated) {
        r.cta = { label: 'CREATE ACCOUNT', fill: true, disabled: false };
        r.cta2 = { label: 'SIGN IN' };
        r.fine = 'Create an account to get tickets.';
      } else {
        r.cta = { label: buyLabel, fill: true, disabled: false };
      }
      r.escrow = greenlit
        ? 'This one is already greenlit. Your money goes to escrow and is released to the host 24h after doors.'
        : 'Your money sits with Portal until 24h after doors, then goes to the host.';
      break;
    }
  }

  // Resume / recover paths override the CTA on the base purchase variants.
  if (!ignoreApplication && app && ['guaranteed', 'starter', 'curated', 'tiered', 'pwyw', 'free'].includes(variant)) {
    if (appStatus === 'pending_payment') {
      r.resume = { kind: 'pending_payment', application: app };
      r.bandRight = 'FINISH PAYING';
      r.cta = { label: `FINISH PAYING — ${money(totalCents)}`, fill: true, disabled: false };
      r.mobCta = 'FINISH PAYING';
    } else if (appStatus === 'payment_failed') {
      r.resume = { kind: 'payment_failed', application: app };
      r.band = 'PAYMENT FAILED';
      r.bandRight = 'SPOT STILL HELD';
      r.cta = { label: 'RETRY PAYMENT', fill: true, disabled: false };
      r.cta2 = null;
      r.fine = "Your last payment didn't go through. Your spot is still held — retry with another card.";
      r.mobCta = 'RETRY PAYMENT';
      r.mobNote = 'SPOT HELD';
    }
  }

  // A Party Starter still funding shows its progress under any purchase variant.
  if (!r.funding && event?.fundingType === 'party_starter' && event?.status === 'funding' && ['tiered', 'pwyw', 'free', 'curated', 'guaranteed'].includes(variant)) {
    r.funding = fundingBlock(event);
  }

  if (!r.breakdown.show) r.breakdown.lines = [];
  return r;
}

export default railModel;
